// REPORTING SCOPE — the one place the operator app turns `reportingScope` into words.
//
// A PropertyGroup is several PMS listings steered as ONE revenue unit from a promoted hotel, the
// LEAD. That changes what a page's numbers describe, and the operator must never have to wonder:
//
//   * at the LEAD, every pooled figure covers the whole unit → say so, and say how many hotels;
//   * at a MEMBER, revenue and target props arrive null/[] on purpose → say WHERE they live and
//     link there, rather than rendering an empty chart or a zero;
//   * ungrouped (`scope == null`) → say nothing at all. Every helper here returns the "no group"
//     answer for null, so an ungrouped surface is byte-identical to life before groups existed.
//
// The vocabulary is locked (docs/plans/2026-10-16-combined-inventory.md rule 1, which replaced the
// Groups doc's rule 1): on a screen the feature is "Combined inventory", the promoted listing is the
// "lead listing" and the others are "following listings". Never "group", never "merge", never
// "revenue unit". Identifiers (`groupName`, `reportingScope`) keep the old word.
//
// A combined inventory has NO NAME OF ITS OWN: it is called by its lead listing's name, so
// `groupName` always equals `leadPropertyName` (server: PropertyGroup#name). A sentence that
// would name both ("Prices for X are set at its lead listing, X") names the lead once and calls the
// combined inventory "this combined inventory".
import type { ReportingScope } from '@/types/shared'

export type MaybeScope = ReportingScope | null | undefined

// Narrowed on `kind`, so ruling one out leaves the OTHER kind (plus null) rather than `never`.
// A page that early-returns on `isDeferredScope` still has a real scope afterwards — the lead's.
export type UnitScope = ReportingScope & { kind: 'unit' }
export type DeferredScope = ReportingScope & { kind: 'deferred' }

/** This property leads a group — the figures on the page are the unit's. */
export function isUnitScope(scope: MaybeScope): scope is UnitScope {
  return scope?.kind === 'unit'
}

/**
 * This property is a member — a surface that reports revenue or targets has DEFERRED to the lead.
 * Not "empty", not "zero": the numbers exist, one hotel over.
 */
export function isDeferredScope(scope: MaybeScope): scope is DeferredScope {
  return scope?.kind === 'deferred'
}

/**
 * How many hotels the GROUP holds.
 *
 * `propertyCount` is 0 at a member by contract (a member's page reports nothing), so it cannot be
 * used to describe the group itself. `members` is full on BOTH sides, so it is the authority; the
 * count is only a fallback for a payload that somehow omitted the list.
 */
export function groupHotelCount(scope: MaybeScope): number {
  if (!scope) return 0
  return scope.members.length || scope.propertyCount
}

/** "2 listings", "1 listing". Same wording as the settings page (lib/groups#countListings). */
export function countListings(n: number): string {
  return n === 1 ? '1 listing' : `${n} listings`
}

/**
 * Whether a caption's subject takes a plural verb — "Occupancy and pace COVER", but "Every override
 * listed here COVERS".
 *
 * A caption is built from a caller-supplied subject, and one hardcoded verb is wrong half the time:
 * a compound subject is plural, while an "Every …" subject is grammatically singular however many
 * nouns follow it. Both shapes are in use, so the agreement is derived rather than assumed.
 */
export function subjectIsPlural(subject: string): boolean {
  const trimmed = subject.trim()
  // "Every room, base rate and guardrail" — a distributive singular, whatever follows it.
  if (/^every\b/i.test(trimmed)) return false
  if (/\band\b/i.test(trimmed)) return true
  const lastWord = trimmed.split(/\s+/).pop() ?? ''
  return /s$/i.test(lastWord) && !/ss$/i.test(lastWord)
}

/**
 * The caption that goes wherever a POOLED number is shown at a lead: how many listings. Null when
 * there is nothing to say (ungrouped, or a group reduced to its lead alone: "covers 1 hotel" is
 * noise, not information). It is the lead's own page, so the combined inventory is "this" one.
 */
export function unitCoverageSentence(scope: MaybeScope): string | null {
  if (!isUnitScope(scope)) return null
  const hotels = groupHotelCount(scope)
  if (hotels < 2) return null
  return `Figures on this page cover all ${countListings(hotels)} in this combined inventory.`
}

/**
 * What a MEMBER is told about a deferred surface. Worded exactly like the backend's own redirect
 * notice (PricingManagedAtLead#pricing_managed_at_lead_notice) so the flash an operator reads on
 * the way to the lead and the card they read on the way back say the same thing.
 */
export function managedAtLeadSentence(scope: MaybeScope): string | null {
  if (!scope) return null
  if (!scope.leadPropertyName) return 'Prices for this combined inventory are set at its lead listing.'
  return `Prices for this combined inventory are set at its lead listing, ${scope.leadPropertyName}.`
}

/**
 * The lead listing by name, or "the lead listing" when the person viewing cannot open it (a limited
 * manager on a following listing, change safety R4.3): the server then sends no name and no link, and
 * the page still says where prices are set without naming a hotel they were not given.
 */
export function leadLabel(scope: MaybeScope): string {
  return scope?.leadPropertyName ?? 'the lead listing'
}

/**
 * THE SAME PAGE, AT THE LEAD.
 *
 * The lead is a hotel this page is not rendered for, so its URL cannot be derived from
 * `property.basePath` and — under /a/:account_slug/properties/:property_slug — cannot be assembled
 * from an id either: neither slug is a fact the frontend holds. The backend serves the whole base
 * (`ReportingScope.leadBasePath`) and this appends a FIXED suffix ("", "/targets", …) so a member's
 * card can offer the equivalent surface rather than dumping the operator on a dashboard.
 *
 * The null-ness is in the TYPE rather than in the caller's string interpolation, so a caller cannot
 * turn a missing path into the literal href "null". Null with no scope, and null when the person
 * viewing cannot open the lead (the server sends no `leadBasePath` then, change safety R4.3): a
 * link to a hotel they cannot open would be a 404 that names it.
 */
export function leadPath(scope: MaybeScope, suffix = ''): string | null {
  if (!scope?.leadBasePath) return null
  return `${scope.leadBasePath}${suffix}`
}

/** Members other than the lead, in the group's own order. */
export function followerMembers(scope: MaybeScope) {
  return scope ? scope.members.filter((m) => !m.isLead) : []
}

/**
 * WHETHER A ROOM ROW SHOULD WEAR A HOTEL LABEL.
 *
 * Derived from the rows themselves, not from `reportingScope`: the label exists to disambiguate,
 * and there is nothing to disambiguate until the list actually spans more than one hotel. That
 * makes it automatically absent for an ungrouped property (one hotel), for a group reduced to its
 * lead, and for a filtered slice that happens to hold one hotel's rooms.
 */
export function spansHotels(rows: readonly { propertyId?: number | null }[]): boolean {
  const seen = new Set<number>()
  for (const row of rows) {
    if (row.propertyId == null) continue
    seen.add(row.propertyId)
    if (seen.size > 1) return true
  }
  return false
}
